import React, { useState, useEffect } from 'react'
import { useForm, usePage, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Key, Plus, Copy, Check, Clock, Shield, ArrowLeft } from 'lucide-react'
import { Link } from '@inertiajs/react'

type ApiToken = {
  id: number
  name: string
  tokenPrefix: string
  scope: string
  scopeLabel: string
  expiresAt: string | null
  expiresAtLabel: string
  lastUsedAt: string | null
  createdAt: string
  isActive: boolean
  isExpired: boolean
  isRevoked: boolean
}

type NewToken = {
  id: number
  name: string
  rawToken: string
}

type Props = {
  tokens: ApiToken[]
  maxTokens: number
  canCreate: boolean
  newToken?: NewToken
  errors?: Record<string, string[]>
}

export default function ApiTokens({ tokens, maxTokens, canCreate, newToken, errors = {} }: Props) {
  const { flash } = usePage().props
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    name: '',
    scope: 'read_only',
    expires_in: '90',
  })

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice as string)
    if (flash?.alert) toast.error(flash.alert as string)
  }, [flash?.notice, flash?.alert])

  // 新規トークン作成後、ダイアログを閉じる
  useEffect(() => {
    if (newToken) {
      setCreateDialogOpen(false)
      reset()
    }
  }, [newToken])

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    post('/settings/api_tokens')
  }

  const handleCopy = () => {
    if (newToken) {
      navigator.clipboard.writeText(newToken.rawToken)
      setCopied(true)
      toast.success('トークンをコピーしました')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleRevoke = (tokenId: number) => {
    router.delete(`/settings/api_tokens/${tokenId}`)
  }

  const registryHost = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'localhost:5001'
    : 'registry.konreji.com'

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/settings" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">APIトークン</h1>
            <p className="text-muted-foreground">
              CI/CDパイプライン（GitHub Actions等）でdocker loginに使用するトークンを管理します
            </p>
          </div>
        </div>

        {/* 新規作成トークン表示 */}
        {newToken && (
          <Alert className="border-primary/50 bg-primary/5">
            <Key className="h-5 w-5 text-primary" />
            <AlertTitle className="text-lg">トークン「{newToken.name}」を作成しました</AlertTitle>
            <AlertDescription className="mt-2 space-y-3">
              <p className="text-sm text-destructive font-medium">
                このトークンは一度しか表示されません。必ずコピーして安全な場所に保存してください。
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all">
                  {newToken.rawToken}
                </code>
                <Button variant="outline" size="icon" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="p-3 bg-muted/50 rounded-md">
                <p className="text-sm font-medium mb-2">使用方法:</p>
                <code className="text-sm">
                  docker login {registryHost} -u your-username -p {newToken.rawToken.substring(0, 8)}...
                </code>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* トークン作成カード */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  アクセストークン
                </CardTitle>
                <CardDescription>
                  {tokens.length} / {maxTokens} 個使用中
                </CardDescription>
              </div>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button disabled={!canCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    新規作成
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>新しいAPIトークンを作成</DialogTitle>
                    <DialogDescription>
                      CI/CDパイプラインで使用するアクセストークンを作成します
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">トークン名</Label>
                      <Input
                        id="name"
                        value={data.name}
                        onChange={(e) => setData('name', e.target.value)}
                        placeholder="例: GitHub Actions"
                        maxLength={50}
                        required
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive">{errors.name[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="scope">スコープ</Label>
                      <Select
                        id="scope"
                        value={data.scope}
                        onChange={(e) => setData('scope', e.target.value)}
                      >
                        <option value="read_only">Read (pull のみ)</option>
                        <option value="read_write">Read/Write (pull + push)</option>
                        <option value="full">Full (pull + push + delete)</option>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {data.scope === 'read_only' && 'イメージのダウンロードのみ可能'}
                        {data.scope === 'read_write' && 'イメージのダウンロード・アップロード可能'}
                        {data.scope === 'full' && 'タグの削除を含む全操作可能'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="expires_in">有効期限</Label>
                      <Select
                        id="expires_in"
                        value={data.expires_in}
                        onChange={(e) => setData('expires_in', e.target.value)}
                      >
                        <option value="30">30日</option>
                        <option value="90">90日</option>
                        <option value="365">1年</option>
                        <option value="never">無期限</option>
                      </Select>
                    </div>

                    <DialogFooter>
                      <Button type="submit" disabled={processing}>
                        {processing ? '作成中...' : 'トークンを作成'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {tokens.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>APIトークンはまだありません</p>
                <p className="text-sm">「新規作成」ボタンからトークンを作成してください</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>名前</TableHead>
                    <TableHead>トークン</TableHead>
                    <TableHead>スコープ</TableHead>
                    <TableHead>有効期限</TableHead>
                    <TableHead>最終使用</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((token) => (
                    <TableRow key={token.id} className={!token.isActive ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">{token.name}</TableCell>
                      <TableCell>
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {token.tokenPrefix}...
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          token.scope === 'full' ? 'destructive' :
                          token.scope === 'read_write' ? 'default' : 'secondary'
                        }>
                          {token.scopeLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {token.isExpired ? (
                          <span className="text-destructive flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            期限切れ
                          </span>
                        ) : (
                          token.expiresAtLabel
                        )}
                      </TableCell>
                      <TableCell>
                        {token.lastUsedAt
                          ? format(new Date(token.lastUsedAt), 'yyyy/MM/dd HH:mm', { locale: ja })
                          : '未使用'
                        }
                      </TableCell>
                      <TableCell>
                        {token.isRevoked ? (
                          <Badge variant="outline">無効化済み</Badge>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-destructive">
                                無効化
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>トークンを無効化しますか？</AlertDialogTitle>
                                <AlertDialogDescription>
                                  「{token.name}」を無効化すると、このトークンを使用した認証は
                                  即座に失敗するようになります。この操作は取り消せません。
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>キャンセル</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRevoke(token.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  無効化
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* 使い方 */}
        <Card>
          <CardHeader>
            <CardTitle>使い方</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Docker CLI</h4>
              <code className="block p-3 bg-muted rounded-md text-sm">
                docker login {registryHost} -u your-username -p knrj_xxxxx...
              </code>
            </div>
            <div>
              <h4 className="font-medium mb-2">GitHub Actions</h4>
              <pre className="p-3 bg-muted rounded-md text-sm overflow-x-auto">
{`- name: Login to Container Registry
  uses: docker/login-action@v3
  with:
    registry: ${registryHost}
    username: \${{ secrets.KONREJI_USERNAME }}
    password: \${{ secrets.KONREJI_TOKEN }}`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
